import React, { useEffect, useState } from "react";
import { useApp } from "../context/AppContext";
import { X, Smartphone } from "lucide-react";
import { toast } from "sonner";

export const AuthModal = ({ open, onClose }) => {
  const { loginCustomer, setRoute } = useApp();
  const [phone, setPhone] = useState("");

  useEffect(() => {
    if (!open) {
      setPhone("");
    }
  }, [open]);

  if (!open) return null;

  // Strict Indian Mobile Number Validation Function
  const isValidIndianMobile = (number) => {
    // 1. Must be exactly 10 digits
    if (!/^\d{10}$/.test(number)) {
      return { valid: false, message: "Please enter a valid 10-digit number" };
    }

    // 2. Must start with 6, 7, 8, or 9
    if (!/^[6-9]/.test(number)) {
      return { valid: false, message: "Indian mobile numbers must start with 6, 7, 8, or 9" };
    }

    // 3. Block same digits repeated 10 times (e.g. 9999999999, 8888888888, 7777777777)
    if (/^(\d)\1{9}$/.test(number)) {
      return { valid: false, message: "Invalid mobile number pattern" };
    }

    // 4. Block common fake sequence numbers
    const fakeSequences = ["1234567890", "9876543210", "0123456789", "0987654321"];
    if (fakeSequences.includes(number)) {
      return { valid: false, message: "Please enter your real mobile number" };
    }

    return { valid: true };
  };

  const handleDirectLogin = (e) => {
    e.preventDefault();

    // Check Mobile Number Rules
    const validation = isValidIndianMobile(phone);
    if (!validation.valid) {
      toast.error(validation.message);
      return;
    }

    // Success -> Direct Login
    loginCustomer(phone);
    toast.success(`Welcome back! (+91 ${phone})`);
    onClose();
    setRoute("customer-hub");
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      data-testid="auth-modal"
    >
      {/* Backdrop */}
      <button
        aria-label="close backdrop"
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal Box */}
      <div className="relative card w-full max-w-md p-8 bg-white rounded-2xl shadow-xl">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-[var(--ink-3)] hover:text-[var(--ink-1)] transition-colors"
          data-testid="auth-modal-close"
          aria-label="close"
        >
          <X size={18} />
        </button>

        <div className="mb-6">
          <h3 className="font-display text-2xl text-[var(--ink-1)] mt-1 font-semibold">
            Enter your mobile number
          </h3>
          <p className="text-sm text-[var(--ink-2)] mt-1">
            Enter a valid mobile number to log in instantly.
          </p>
        </div>

        <form onSubmit={handleDirectLogin} className="space-y-5">
          <div>
            <label className="text-sm font-medium text-[var(--ink-1)] block mb-2">
              Mobile number
            </label>
            <div className="flex items-center gap-2">
              <span className="px-3 py-3 rounded-xl border border-[var(--line-strong)] bg-gray-50 text-[var(--ink-2)] font-medium text-sm">
                +91
              </span>
              <div className="relative flex-1">
                <Smartphone
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-4)]"
                />
                <input
                  className="field pl-10 w-full"
                  placeholder="9876543210"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  data-testid="auth-phone-input"
                  inputMode="numeric"
                  maxLength={10}
                  autoFocus
                />
              </div>
            </div>
          </div>

          <button
            type="submit"
            className="btn-primary w-full justify-center py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl transition-all"
            data-testid="auth-direct-login-button"
          >
            Continue / Login
          </button>
        </form>
      </div>
    </div>
  );
};